import { v4 } from "uuid";

import type { ICol, IRow, ISection } from "../../../interfaces";
import type { INMEAAutomationModalData } from "../../../interfaces/Modals";
import { deepClone } from "../../../utils";
import { parseNMEATimeToDisplay } from "../../../utils/timeUtils/parseNMEATime";
import { convertDDMMtoMMDD } from "../../../utils/timeUtils/convertDateFormat";
import {
  clearNMEAProperties,
  clearSingleValue as clearValue,
} from "../../utils/dynamicRows/clearValues";
import { sortRowsByDateTime } from "../../utils/sortByDateTime";
import dayjs from "dayjs";

export interface INMEARow {
  cols: Record<string, ICol>;
  dateTime?: string;
  dateTimeLT?: string;
  dateTimeUTC?: string;
  timeLT?: string;
  timeUTC?: string;
  NMEAId: string;
  selected?: boolean;
}

export interface INMEASection {
  rows: INMEARow[];
  originalSectionIndex: number;
}

interface IParseNMEASectionsParams {
  originalSections: ISection[];
  newData?: Array<Record<string, any>>;
  sectionIndex?: number;
  setValue?: (id: string, value: any) => void;
  rowIndex?: number;
  selectedSections?: INMEASection[];
  sortOrder?: "asc" | "desc";
  location?: "anchor" | "underway";
  preview?: boolean;
  dropRowsWithoutNMEAId?: boolean;
}

export interface IAmendCandidateInput {
  /** Index of the section in lbentry.sections */
  sectionIndex: number;
  /** Index of the row within that section */
  rowIndex: number;
  /** The raw NMEA data row to apply to this row */
  nmeaDataRow: Record<string, any>;
}

interface IApplyNMEAAmendmentsParams {
  originalSections: ISection[];
  candidates: IAmendCandidateInput[];
  setValue: (id: string, value: any) => void;
  sortOrder?: "asc" | "desc";
  location?: "anchor" | "underway";
}

const getColsRecord = (cols: unknown): Record<string, ICol> => {
  if (!cols) return {};
  if (Array.isArray(cols)) {
    return cols.reduce<Record<string, ICol>>((acc, col, index) => {
      acc[String(index)] = col as ICol;
      return acc;
    }, {});
  }
  return cols as Record<string, ICol>;
};

const getAllRowCols = (row: IRow): ICol[] => {
  const cols = row.cols ? Object.values(getColsRecord(row.cols)) : [];
  const subrowCols =
    row.subrows?.flatMap(subrow => Object.values(getColsRecord(subrow.cols))) ??
    [];
  return [...cols, ...subrowCols];
};

const getRowTimeCol = (row: IRow): ICol | undefined => {
  return getAllRowCols(row).find(
    col => col.NMEA === "_ROW_DATE_TIME" || col.NMEA === "ROW_TIME",
  );
};

const rowHasNMEAColumns = (row: IRow): boolean => {
  return getAllRowCols(row).some(col => !!col.NMEA);
};

const buildUpdatedCols = (
  cols: Record<string, ICol>,
  dataRow: Record<string, any>,
  setValue: (id: string, value: any) => void,
  location?: "anchor" | "underway",
): Record<string, ICol> => {
  const updatedCols: Record<string, ICol> = {};

  Object.entries(cols).forEach(([key, col]) => {
    const typedCol = col;

    // For existing rows, respect their individual column locations.
    if (typedCol.location === "anchor" && typedCol.underway === true) {
      updatedCols[key] = typedCol;
      return;
    }

    const NMEAKey = getNMEAKey(typedCol, dataRow);
    if (typedCol.NMEA && dataRow[NMEAKey]) {
      const { value: newValue, NMEAValue } = parseNMEAValue(typedCol, dataRow);
      updatedCols[key] = {
        ...typedCol,
        value: newValue,
        source: "auto",
        NMEAId: dataRow.NMEAId,
        NMEAValue,
        ...(!typedCol.location && location && { location }),
      };
      setValue(key, newValue);
      return;
    }

    updatedCols[key] = typedCol;
  });

  return updatedCols;
};

const buildNewColsFromTemplate = (
  cols: Record<string, ICol>,
  dataRow: Record<string, any>,
  setValue: (id: string, value: any) => void,
  location?: "anchor" | "underway",
): Record<string, ICol> => {
  const newCols: Record<string, ICol> = {};

  Object.values(cols).forEach(col => {
    const typedCol = col;
    const id = v4();

    // Handle underway-specific columns when location is "anchor".
    if (location === "anchor" && typedCol.underway === true) {
      const clearedValue = clearValue(typedCol);
      newCols[id] = {
        ...typedCol,
        value: clearedValue,
        location: "anchor",
      };
      setValue(id, clearedValue);
      return;
    }

    const NMEAKey = getNMEAKey(typedCol, dataRow);
    if (typedCol.NMEA && dataRow[NMEAKey]) {
      const { value: newValue, NMEAValue } = parseNMEAValue(typedCol, dataRow);
      newCols[id] = {
        ...typedCol,
        value: newValue,
        source: "auto",
        NMEAId: dataRow.NMEAId,
        NMEAValue,
        ...(location && { location }),
      };
      setValue(id, newValue);
      return;
    }

    newCols[id] = {
      ...typedCol,
      ...(location && { location }),
    };
    setValue(id, typedCol.value);
  });

  return newCols;
};

/**
 * Creates a clean row template from the first row, clearing all values
 */
const createCleanRowTemplate = (
  templateRow: IRow,
  location?: "anchor" | "underway",
): IRow => {
  const cleanRow = deepClone(templateRow);

  const cleanColsMapper = (
    cols: Record<string, ICol>,
  ): Record<string, ICol> => {
    const mappedCols: Record<string, ICol> = {};

    Object.values(cols).forEach(col => {
      const typedCol = col;
      const cleanCol = clearNMEAProperties(typedCol) as ICol;
      cleanCol.value = clearValue(col);

      if (location === "anchor" && typedCol.underway === true) {
        cleanCol.value = clearValue(col);
        cleanCol.location = "anchor";
      } else {
        delete cleanCol.location;
      }

      mappedCols[v4()] = cleanCol;
    });

    return mappedCols;
  };

  if (cleanRow.cols) {
    cleanRow.cols = cleanColsMapper(getColsRecord(cleanRow.cols));
  }

  if (cleanRow.subrows?.length) {
    cleanRow.subrows = cleanRow.subrows.map((subrow: { cols: unknown }) => ({
      ...subrow,
      cols: cleanColsMapper(getColsRecord(subrow.cols)) as any,
    }));
  }

  cleanRow.isNMEAAffected = false;
  return cleanRow;
};

/**
 * Parses NMEA values to match cell formats
 */
// Parse nmea value to match value format of the column
const parseNMEAValue = (
  typedCol: ICol,
  dataRow: Record<string, any>,
): {
  value: string | Record<string, string>;
  NMEAValue: string | Record<string, string>;
} => {
  const NMEAKey = getNMEAKey(typedCol, dataRow);

  if (NMEAKey && dataRow[NMEAKey]) {
    let newValue: any = dataRow[NMEAKey];

    // Parse nmea value to match value format of the column
    if (typedCol.unit === "date" && newValue) {
      newValue = convertDDMMtoMMDD(newValue);
    }
    if (typedCol.unit === "24lock" && newValue) {
      if (
        typeof newValue === "object" &&
        newValue !== null &&
        "time" in newValue
      ) {
        return {
          value: {
            time: (newValue as { time?: string }).time,
            type:
              (newValue as { type?: string }).type ??
              typedCol.value?.type ??
              "",
          },
          NMEAValue: dataRow[NMEAKey],
        };
      }

      let splitted, time;
      const stringValue = String(newValue);
      if (
        stringValue.includes("T") &&
        dayjs(stringValue.split("T")?.[0]).isValid()
      ) {
        splitted = stringValue.split("T")[1].split(":");
        time = splitted[0] + ":" + splitted[1];
      } else if (
        stringValue.includes("LT") &&
        dayjs(stringValue.split("LT")?.[0]).isValid()
      ) {
        time = dayjs(stringValue.split("LT")?.[0]).format("HH:mm:ss");
      } else if (stringValue.split(" ").length > 1) {
        splitted = stringValue.split(" ")[3].split(":");
        time = splitted[0] + ":" + splitted[1];
      } else {
        const match = stringValue.match(/(\d{2}:\d{2}(?::\d{2})?)/);
        time = match
          ? match[1]
          : (parseNMEATimeToDisplay(stringValue) ?? stringValue);
      }

      newValue = {
        time,
        type: typedCol.value?.type ?? "",
      };
    }
    if (typedCol.unit === "temperature" && newValue) {
      newValue = {
        value: Number(String(newValue).replace(/[^\d.]/g, "")) || null,
        unit: "celsius",
      };
    }
    if (typedCol.unit === "pressure" && newValue) {
      newValue = {
        value: Number(String(newValue).replace(/[^\d.]/g, "")) || null,
        unit: "mbar",
      };
    }
    return { value: newValue, NMEAValue: dataRow[NMEAKey] };
  }
  return { value: "", NMEAValue: "" };
};

/**
 * Finds an existing row with the same NMEA ID
 */
const findRowByNMEAId = (
  rows: IRow[],
  nmeaId: string,
): { row: IRow; index: number } | null => {
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const hasMatchingNMEAId = getAllRowCols(row).some(
      col => col.NMEAId === nmeaId,
    );
    if (hasMatchingNMEAId) {
      return { row, index: i };
    }
  }
  return null;
};

const findNMEARowByTime = (
  rows: IRow[],
  nmeaTime: string,
): { row: IRow; index: number } | null => {
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const hasNMEAId = getAllRowCols(row).some(col => col.NMEAId);
    const timeCol = getRowTimeCol(row);
    const timeValue =
      typeof timeCol?.value === "object" && timeCol?.value?.time
        ? timeCol.value.time
        : String(timeCol?.value ?? "");

    if (hasNMEAId && timeValue === nmeaTime) {
      return { row, index: i };
    }
  }
  return null;
};

const rowHasNMEAId = (row: IRow): boolean => {
  return getAllRowCols(row).some(col => !!col.NMEAId);
};

/**
 * Updates an existing row with NMEA data
 */
const updateRowWithNMEAData = (
  existingRow: IRow,
  dataRow: Record<string, any>,
  setValue: (id: string, value: any) => void,
  location?: "anchor" | "underway",
): IRow => {
  const updatedRow = deepClone(existingRow);

  if (updatedRow.cols) {
    updatedRow.cols = buildUpdatedCols(
      getColsRecord(updatedRow.cols),
      dataRow,
      setValue,
      location,
    );
  }

  if (updatedRow.subrows?.length) {
    updatedRow.subrows = updatedRow.subrows.map(
      (subrow: { cols: unknown }) => ({
        ...subrow,
        cols: buildUpdatedCols(
          getColsRecord(subrow.cols),
          dataRow,
          setValue,
          location,
        ) as any,
      }),
    );
  }

  updatedRow.isNMEAAffected = true;
  return updatedRow;
};

/**
 * Creates a new row with NMEA data
 */
const createRowWithNMEAData = (
  templateRow: IRow,
  dataRow: Record<string, any>,
  setValue: (id: string, value: any) => void,
  location?: "anchor" | "underway",
): IRow => {
  const newRow = createCleanRowTemplate(templateRow, location);

  if (newRow.cols) {
    newRow.cols = buildNewColsFromTemplate(
      getColsRecord(newRow.cols),
      dataRow,
      setValue,
      location,
    );
  }

  if (newRow.subrows?.length) {
    newRow.subrows = newRow.subrows.map((subrow: { cols: unknown }) => ({
      ...subrow,
      cols: buildNewColsFromTemplate(
        getColsRecord(subrow.cols),
        dataRow,
        setValue,
        location,
      ) as any,
    }));
  }

  newRow.isNMEAAffected = true;
  return newRow;
};

interface IMergeNMEADataParams {
  section: ISection;
  sectionIndex: number;
  data: Array<Record<string, any>>;
  setValue: (id: string, value: any) => void;
  targetSectionIndex?: number;
  targetRowIndex?: number;
  sortOrder: "asc" | "desc";
  location?: "anchor" | "underway";
  dropRowsWithoutNMEAId?: boolean;
}

/**
 * Merges NMEA data with existing rows, preserving manual entries
 * Updates existing rows with matching NMEA IDs or creates new ones
 */
const mergeNMEADataWithExistingRows = ({
  section,
  sectionIndex,
  data,
  setValue,
  targetSectionIndex,
  targetRowIndex,
  sortOrder,
  location,
  dropRowsWithoutNMEAId,
}: IMergeNMEADataParams): ISection => {
  const templateRow = section.rows[0];
  if (!templateRow) return section;

  // Start with existing rows (optionally excluding placeholder rows without NMEA IDs)
  const rowsWithSaved = section.rows.map((row, index) => ({
    row,
    saved: section.savedRows?.[index] ?? false,
  }));
  const shouldDropRowsWithoutNMEAId =
    dropRowsWithoutNMEAId &&
    targetSectionIndex === undefined &&
    targetRowIndex === undefined;
  const baseRowsWithSaved = shouldDropRowsWithoutNMEAId
    ? rowsWithSaved.filter(item => rowHasNMEAId(item.row))
    : rowsWithSaved;

  const mergedRows: IRow[] = baseRowsWithSaved.map(item => item.row);
  const mergedSavedRows: boolean[] = baseRowsWithSaved.map(item => item.saved);

  // Process each NMEA data entry
  data.forEach(dataRow => {
    // Skip if this is a specific target and doesn't match
    if (
      targetSectionIndex !== undefined &&
      sectionIndex !== targetSectionIndex
    ) {
      return;
    }

    // // For specific row updates, only process the first data entry
    // if (
    //   targetSectionIndex !== undefined &&
    //   targetRowIndex !== undefined &&
    //   dataIndex > 0
    // ) {
    //   return;
    // }

    const nmeaId = dataRow.NMEAId;
    const nmeaTime = dataRow._ROW_DATE_TIME;
    if (!nmeaId) return;

    // Try to find existing row by Time or nmeaId
    const existingRowData =
      (nmeaTime && findNMEARowByTime(mergedRows, nmeaTime)) ||
      findRowByNMEAId(mergedRows, nmeaId);

    if (existingRowData) {
      // Update existing row – preserve its saved state
      mergedRows[existingRowData.index] = updateRowWithNMEAData(
        existingRowData.row,
        dataRow,
        setValue,
        location,
      );
    } else {
      // Create new row with NMEA data – mark as unsaved
      const newRow = createRowWithNMEAData(
        templateRow,
        dataRow,
        setValue,
        location,
      );
      mergedRows.push(newRow);
      mergedSavedRows.push(false);
    }
  });

  // Sort rows and reorder savedRows to match, using row-reference as the key
  const sortedRows = sortRowsByDateTime(
    mergedRows,
    sortOrder === "asc",
    section.dynamicRows,
  );

  const savedByRow = new Map<IRow, boolean>(
    mergedRows.map((row, i) => [row, mergedSavedRows[i] ?? true]),
  );
  const sortedSavedRows = sortedRows.map(row => savedByRow.get(row) ?? false);

  return {
    ...section,
    rows: sortedRows,
    savedRows: sortedSavedRows,
  };
};

const getNMEARowDateTimeValue = (row: INMEARow): number => {
  const dateTime = row?.dateTimeLT ?? row?.timeLT ?? row?.dateTime;
  if (!dateTime) return 0;
  try {
    const date = new Date(dateTime);
    if (!isNaN(date.getTime())) {
      return date.getTime();
    }
    const normalized = normalizeTimeLT(dateTime);
    if (normalized) {
      const timeOnly = new Date(`1970-01-01T${normalized}`);
      if (!isNaN(timeOnly.getTime())) return timeOnly.getTime();
    }
  } catch (error) {
    console.warn("Error parsing dateTime:", dateTime, error);
  }
  return 0;
};

const getNMEARowStableId = (row: INMEARow): string =>
  row.NMEAId || row.dateTimeLT || row.timeLT || row.dateTime || "";

/**
 * Sorts NMEA rows chronologically by time (0000-2359) using the dateTime property.
 * Handles INMEARow objects with dateTime property.
 *
 * @param rows - Array of NMEA rows to sort by time
 * @param ascending - Whether to sort oldest-to-newest instead of newest-to-oldest
 * @returns Sorted array of NMEA rows in chronological order
 */
export const sortNMEARowsByTime = (
  rows: INMEARow[],
  ascending: boolean = false,
): INMEARow[] => {
  return [...rows].sort((a, b) => {
    const timeA = getNMEARowDateTimeValue(a);
    const timeB = getNMEARowDateTimeValue(b);
    return ascending ? timeA - timeB : timeB - timeA;
  });
};

export const remapNMEASelectionIndices = (
  rows: INMEARow[],
  selectedIndices: number[],
  ascending: boolean,
): number[] => {
  if (!selectedIndices.length || !rows.length) return [];

  const currentSortedRows = sortNMEARowsByTime(rows, !ascending);
  const selectedIds = new Set(
    selectedIndices
      .map(index => currentSortedRows[index])
      .filter((row): row is INMEARow => Boolean(row))
      .map(getNMEARowStableId)
      .filter(Boolean),
  );

  if (!selectedIds.size) return [];

  return sortNMEARowsByTime(rows, ascending)
    .map((row, index) => ({
      id: getNMEARowStableId(row),
      index,
    }))
    .filter(row => selectedIds.has(row.id))
    .map(row => row.index);
};

export const getNMEARowNormalizedTime = (row: INMEARow): string | null =>
  normalizeTimeLT(row.timeLT ?? row.dateTimeLT ?? row.dateTime ?? "");

export const sectionHasNMEATimeRows = (rows: INMEARow[]): boolean =>
  rows.some(row => Boolean(getNMEARowNormalizedTime(row)));

type TMaritimeTimeQuery =
  | { kind: "all" }
  | { kind: "invalid" }
  | { kind: "hour"; start: string; end: string }
  | { kind: "exact"; target: string }
  | { kind: "range"; start: string; end: string };

const isValidHourMinute = (
  token: string,
  allowHourOnly: boolean = false,
): boolean => {
  if (!/^\d+$/.test(token)) return false;
  if (allowHourOnly && token.length === 2) {
    const hours = Number(token);
    return hours >= 0 && hours <= 23;
  }
  if (token.length !== 4) return false;
  const hours = Number(token.slice(0, 2));
  const minutes = Number(token.slice(2, 4));
  return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
};

const expandMaritimeTokenToRange = (
  token: string,
  isEnd: boolean,
): { start: string; end: string } | null => {
  if (token.length === 2) {
    if (!isValidHourMinute(token, true)) return null;
    return {
      start: `${token}:00:00`,
      end: `${token}:59:59`,
    };
  }
  if (token.length === 4) {
    if (!isValidHourMinute(token)) return null;
    const hh = token.slice(0, 2);
    const mm = token.slice(2, 4);
    return {
      start: `${hh}:${mm}:00`,
      end: `${hh}:${mm}:59`,
    };
  }
  return null;
};

const normalizeQueryToken = (token: string): string => token.trim();

export const parseMaritimeTimeQuery = (query: string): TMaritimeTimeQuery => {
  const trimmed = query.trim();
  if (!trimmed) return { kind: "all" };

  if (!/^[\d\s-]+$/.test(trimmed)) {
    return { kind: "invalid" };
  }

  const tokens = trimmed
    .split(/[\s-]+/)
    .map(normalizeQueryToken)
    .filter(Boolean);

  if (tokens.length === 1) {
    const [token] = tokens;
    if (token.length === 2) {
      const range = expandMaritimeTokenToRange(token, false);
      return range ? { kind: "hour", ...range } : { kind: "invalid" };
    }
    if (token.length === 4) {
      if (!isValidHourMinute(token)) return { kind: "invalid" };
      const hh = token.slice(0, 2);
      const mm = token.slice(2, 4);
      return { kind: "exact", target: `${hh}:${mm}:00` };
    }
    return { kind: "invalid" };
  }

  if (tokens.length === 2) {
    const startRange = expandMaritimeTokenToRange(tokens[0], false);
    const endRange = expandMaritimeTokenToRange(tokens[1], true);
    if (!startRange || !endRange) return { kind: "invalid" };
    if (startRange.start > endRange.end) return { kind: "invalid" };
    return {
      kind: "range",
      start: startRange.start,
      end: endRange.end,
    };
  }

  return { kind: "invalid" };
};

export const getVisibleNMEARowsForQuery = (
  rows: INMEARow[],
  query: string,
): Array<{ row: INMEARow; sortedIndex: number }> => {
  const parsedQuery = parseMaritimeTimeQuery(query);
  const rowsWithIndices = rows.map((row, sortedIndex) => ({
    row,
    sortedIndex,
  }));

  if (parsedQuery.kind === "all" || parsedQuery.kind === "invalid") {
    return rowsWithIndices;
  }

  if (parsedQuery.kind === "hour" || parsedQuery.kind === "range") {
    return rowsWithIndices.filter(({ row }) => {
      const normalizedRowTime = getNMEARowNormalizedTime(row);
      if (!normalizedRowTime) return false;
      return (
        normalizedRowTime >= parsedQuery.start &&
        normalizedRowTime <= parsedQuery.end
      );
    });
  }

  const exactMatches = rowsWithIndices.filter(({ row }) => {
    const normalizedRowTime = getNMEARowNormalizedTime(row);
    return normalizedRowTime === parsedQuery.target;
  });

  let beforeMatch: { row: INMEARow; sortedIndex: number } | null = null;
  let afterMatch: { row: INMEARow; sortedIndex: number } | null = null;

  rowsWithIndices.forEach(candidate => {
    const normalizedRowTime = getNMEARowNormalizedTime(candidate.row);
    if (!normalizedRowTime) return;

    if (normalizedRowTime < parsedQuery.target) {
      if (
        !beforeMatch ||
        normalizedRowTime > (getNMEARowNormalizedTime(beforeMatch.row) ?? "")
      ) {
        beforeMatch = candidate;
      }
    } else if (normalizedRowTime > parsedQuery.target) {
      if (
        !afterMatch ||
        normalizedRowTime <
          (getNMEARowNormalizedTime(afterMatch.row) ?? "99:99:99")
      ) {
        afterMatch = candidate;
      }
    }
  });

  const deduped = new Map<number, { row: INMEARow; sortedIndex: number }>();
  [beforeMatch, ...exactMatches, afterMatch].filter(Boolean).forEach(match => {
    deduped.set(match.sortedIndex, match);
  });

  return Array.from(deduped.values()).sort(
    (a, b) => a.sortedIndex - b.sortedIndex,
  );
};

/**
 * Parses and updates the NMEA sections with new data.
 *
 * @param originalSections - The original sections to be updated.
 * @param newData - Optional new data to populate the sections.
 * @param sectionIndex - The index of the section to be updated.
 * @param setValue - Optional function to set values for specific columns.
 * @param rowIndex - The index of the row to be updated.
 * @param selectedSections - Optional selected sections to process.
 * @returns An array of updated sections.
 */
export const parseNMEASections = ({
  originalSections,
  newData,
  sectionIndex,
  setValue,
  rowIndex,
  selectedSections,
  sortOrder = "asc",
  location,
  preview,
  dropRowsWithoutNMEAId,
}: IParseNMEASectionsParams): ISection[] => {
  let data = newData;
  if (selectedSections) {
    data = selectedSections
      .flatMap(section => {
        return section.rows
          .filter(row => row !== null && row !== undefined)
          .map(row => {
            const newRow: Record<string, any> = {
              NMEAId: row.NMEAId,
              selected: row.selected,
              _TEST: true,
              _originalSectionIndex: section.originalSectionIndex, // Add section info to data
            };
            Object.entries(row.cols).forEach(([key, col]) => {
              if (col.NMEA) newRow[col.NMEA] = col.value as string;
            });
            return newRow;
          });
      })
      .filter(r => r.selected);
  }

  // Get the section indices that should be processed based on selectedSections or newData
  const targetSectionIndices = new Set<number>();
  if (selectedSections) {
    // Only add sections that have selected rows to targetSectionIndices
    selectedSections.forEach(section => {
      const hasSelectedRows = section.rows.some(row => row?.selected);
      if (hasSelectedRows) {
        targetSectionIndices.add(section.originalSectionIndex);
      }
    });
  } else if (newData) {
    // Extract originalSectionIndex from newData if available
    newData.forEach(dataRow => {
      if (dataRow._originalSectionIndex !== undefined) {
        targetSectionIndices.add(dataRow._originalSectionIndex);
      }
    });
  }

  return originalSections.map((ogSection, ogIndex) => {
    // Check if this section has any NMEA columns
    const hasNMEAColumns = ogSection.rows.some(row => rowHasNMEAColumns(row));
    // Only process sections that:
    // 1. Have NMEA columns AND
    // 2. Are in the target indices OR when a specific sectionIndex is provided
    const shouldProcessSection =
      hasNMEAColumns &&
      (targetSectionIndices.has(ogIndex) ||
        (sectionIndex !== undefined && sectionIndex === ogIndex));

    if (!shouldProcessSection) {
      // Return section unchanged if it's not a target section
      return ogSection;
    }

    // For sections with setValue (accepting NMEA data), we need to merge with existing rows
    if (setValue && ogSection.dynamicRows && data) {
      // Filter data to only include rows that belong to this specific section
      const sectionSpecificData = data.filter(dataRow => {
        // If no section info in data, include all (fallback behavior)
        if (dataRow._originalSectionIndex === undefined) {
          return true;
        }
        // Only include data that belongs to this section
        return dataRow._originalSectionIndex === ogIndex;
      });

      // Only process if we have data for this section
      if (sectionSpecificData.length > 0) {
        return mergeNMEADataWithExistingRows({
          section: ogSection,
          sectionIndex: ogIndex,
          data: sectionSpecificData,
          setValue,
          targetSectionIndex: sectionIndex,
          targetRowIndex: rowIndex,
          sortOrder,
          location,
          dropRowsWithoutNMEAId,
        });
      } else {
        // No data for this section, return unchanged
        return ogSection;
      }
    }

    // Legacy behavior for preview/non-setValue cases
    const newRows: IRow[] = [];
    // Filter data to only include rows that belong to this specific section
    const sectionSpecificData = data?.filter(dataRow => {
      // If no section info in data, include all (fallback behavior)
      if (dataRow._originalSectionIndex === undefined) {
        return true;
      }
      // Only include data that belongs to this section
      return dataRow._originalSectionIndex === ogIndex;
    });

    const rowsMapper = (row: IRow, rowIdx: number) => {
      const newRow: IRow = deepClone(row);

      const mapLegacyCols = (
        cols: Record<string, ICol>,
      ): Record<string, ICol> => {
        const mappedCols: Record<string, ICol> = {};

        Object.entries(cols).forEach(([key, col]) => {
          if (
            col.NMEA &&
            ((sectionIndex !== undefined &&
              sectionIndex === ogIndex &&
              rowIndex === rowIdx) ||
              sectionIndex === undefined)
          ) {
            const dataIndex = sectionIndex !== undefined ? 0 : rowIdx;
            const effectiveLocation = col.location ?? location;
            const atAnchor = effectiveLocation === "anchor";

            if (atAnchor && col.underway === true) {
              mappedCols[key] = col;
              return;
            }
            const dataRow = sectionSpecificData?.[dataIndex];
            const NMEAKey = dataRow ? getNMEAKey(col, dataRow) : "";
            if (dataRow?.[NMEAKey]) {
              const { value: newValue, NMEAValue } = parseNMEAValue(
                col,
                dataRow,
              );
              const id = v4();
              mappedCols[id] = {
                ...col,
                value: newValue,
                source: "auto",
                NMEAId: sectionSpecificData[dataIndex].NMEAId,
                NMEAValue,
                ...(!col.location &&
                  location && {
                    location: effectiveLocation,
                  }),
              };
              setValue?.(id, newValue);
              newRow.isNMEAAffected = true;
            } else {
              mappedCols[v4()] = { ...col };
            }
          } else {
            mappedCols[key] = col;
          }
        });

        return mappedCols;
      };

      if (newRow.cols) {
        newRow.cols = mapLegacyCols(getColsRecord(newRow.cols));
      }

      if (newRow.subrows?.length) {
        newRow.subrows = newRow.subrows.map((subrow: { cols: unknown }) => ({
          ...subrow,
          cols: mapLegacyCols(getColsRecord(subrow.cols)) as any,
        }));
      }

      newRows.push(newRow);
    };
    if (ogSection.dynamicRows) {
      if (!sectionSpecificData || sectionIndex !== undefined) {
        ogSection.rows.forEach(rowsMapper);
      } else {
        Array(
          sectionSpecificData.filter(el => (setValue ? el.selected : true))
            .length,
        )
          .fill(0)
          .forEach((_, rowIdx) => {
            rowsMapper(ogSection.rows[0], rowIdx);
          });
      }
    } else {
      ogSection.rows.forEach(rowsMapper);
    }
    const rowsToReturn = setValue
      ? sortRowsByDateTime(newRows, sortOrder === "asc", ogSection.dynamicRows)
      : newRows;
    //
    // Sort rows chronologically by time (0000-2359) when setValue is present (data being applied)
    // console.log(preview, "preview", rowsToReturn);

    return {
      ...ogSection,
      rows: rowsToReturn,
    };
  });
};

/**
 * Applies NMEA data directly onto specific existing rows, identified by
 * (sectionIndex, rowIndex) — REPLACING that row's NMEA-tagged column values
 * with the matched NMEA data, rather than inserting a new row.
 */
export const applyNMEAAmendments = ({
  originalSections,
  candidates,
  setValue,
  sortOrder = "asc",
  location,
}: IApplyNMEAAmendmentsParams): ISection[] => {
  const candidatesBySection = new Map<number, IAmendCandidateInput[]>();
  candidates.forEach(candidate => {
    const list = candidatesBySection.get(candidate.sectionIndex) ?? [];
    list.push(candidate);
    candidatesBySection.set(candidate.sectionIndex, list);
  });

  return originalSections.map((section, sectionIndex) => {
    const sectionCandidates = candidatesBySection.get(sectionIndex);
    if (!sectionCandidates?.length) return section;

    const rows = [...section.rows];

    sectionCandidates.forEach(({ rowIndex, nmeaDataRow }) => {
      const row = rows[rowIndex];
      if (!row) return;
      rows[rowIndex] = updateRowWithNMEAData(
        row,
        nmeaDataRow,
        setValue,
        location,
      );
    });

    if (!section.dynamicRows) {
      return { ...section, rows };
    }

    const savedRows = section.savedRows;
    const savedByRow = new Map<IRow, boolean>(
      rows.map((row, i) => [row, savedRows?.[i] ?? false]),
    );
    const sortedRows = sortRowsByDateTime(
      rows,
      sortOrder === "asc",
      section.dynamicRows,
    );
    const sortedSavedRows = sortedRows.map(row => savedByRow.get(row) ?? false);

    return {
      ...section,
      rows: sortedRows,
      savedRows: sortedSavedRows,
    };
  });
};

export const deriveNMEAId = (dataRow: Record<string, any>): string => {
  if (dataRow.NMEAId) return String(dataRow.NMEAId);
  if (dataRow._NMEAId) return String(dataRow._NMEAId);
  const dt = dataRow._ROW_DATE_TIME_LT?.replace(/\s*LT$/, "");
  if (dt) return `nmea-${dt.replace(/\s+/g, "_")}`;
  return v4();
};

export const deriveTimeFromNMEAId = (nmeaId?: string): string | null => {
  const match = nmeaId?.match(/(\d{2}:\d{2}:\d{2})$/);
  return match ? match[1] : null;
};

const flattenNMEAColValue = (col: ICol): unknown => {
  if (col.NMEA === "ROW_TIME" || col.NMEA === "_ROW_DATE_TIME") {
    if (col.NMEAValue) return col.NMEAValue;
    if (
      typeof col.value === "object" &&
      col.value !== null &&
      "time" in col.value
    ) {
      return (col.value as { time?: string }).time ?? col.value;
    }
  }
  return col.value;
};

/** Normalizes a time value to HH:mm:ss for comparison. */
export const normalizeTimeLT = (
  time: string | null | undefined,
): string | null => {
  if (!time) return null;

  const parsed = parseNMEATimeToDisplay(time);
  if (parsed) return parsed;

  const match = String(time).match(/(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return null;

  const [, hours, minutes, seconds = "00"] = match;
  return `${hours}:${minutes}:${seconds}`;
};

export const nmeaTimesMatch = (
  a: string | null | undefined,
  b: string | null | undefined,
): boolean => {
  const normA = normalizeTimeLT(a);
  const normB = normalizeTimeLT(b);
  if (!normA || !normB) return false;
  return normA === normB;
};

export const getRawNMEARowTimeLT = (
  dataRow: Record<string, any>,
): string | null => {
  if (dataRow._ROW_TIME_LT) return String(dataRow._ROW_TIME_LT);
  if (dataRow._ROW_DATE_TIME_LT) {
    const match = String(dataRow._ROW_DATE_TIME_LT).match(
      /(\d{2}:\d{2}(?::\d{2})?)/,
    );
    if (match) return match[1];
  }
  if (dataRow._ROW_DATE_TIME) return String(dataRow._ROW_DATE_TIME);

  const rowTime = dataRow.ROW_TIME;
  if (typeof rowTime === "string" && rowTime) {
    return (
      parseNMEATimeToDisplay(rowTime) ?? normalizeTimeLT(rowTime) ?? rowTime
    );
  }
  if (typeof rowTime === "object" && rowTime !== null && "time" in rowTime) {
    const time = (rowTime as { time?: string }).time;
    return time ? (normalizeTimeLT(time) ?? time) : null;
  }

  return deriveTimeFromNMEAId(dataRow.NMEAId ?? dataRow._NMEAId);
};

export const getClockHourKey = (
  entryDate: string | null,
  timeLT: string,
): string | null => {
  if (!entryDate) return null;

  const dateMatch = entryDate.match(/^(\d{4}-\d{2}-\d{2})/);
  if (!dateMatch) return null;

  const hourMatch = timeLT.match(/(\d{2}):\d{2}/);
  if (!hourMatch) return null;

  return `${dateMatch[1]}T${hourMatch[1]}`;
};

export const getLogbookRowTimeLT = (row: IRow): string | null => {
  const timeCol = getRowTimeCol(row);
  if (!timeCol) return null;

  if (typeof timeCol.value === "object" && timeCol.value?.time) {
    const raw = timeCol.value.time as string;
    return parseNMEATimeToDisplay(raw) ?? raw;
  }

  if (typeof timeCol.value === "string" && timeCol.value) {
    return parseNMEATimeToDisplay(timeCol.value) ?? timeCol.value;
  }

  if (timeCol.NMEAValue && typeof timeCol.NMEAValue === "string") {
    return parseNMEATimeToDisplay(timeCol.NMEAValue) ?? timeCol.NMEAValue;
  }

  return null;
};

/** Flattens saved INMEASection[] into API-shaped rows for amend matching. */
export const flattenSavedNMEADataToRawRows = (
  nmeaData: INMEASection[] | undefined,
): Array<Record<string, any>> => {
  if (!nmeaData?.length) return [];

  return nmeaData.flatMap(section =>
    section.rows.flatMap(row => {
      if (!row) return [];

      const hasTimeCol = Boolean(
        row.cols?._ROW_DATE_TIME || row.cols?.ROW_TIME,
      );
      const hasDateTimeLT = Boolean(row.dateTimeLT);
      const hasTimeLT = Boolean(row.timeLT);
      const hasNMEAId = Boolean(row.NMEAId);
      if (!hasTimeCol && !hasDateTimeLT && !hasTimeLT && !hasNMEAId) {
        return [];
      }

      const flatRow: Record<string, any> = {
        NMEAId: row.NMEAId,
        _originalSectionIndex: section.originalSectionIndex,
      };

      Object.values(row.cols).forEach(col => {
        if (col.NMEA) {
          flatRow[col.NMEA] = flattenNMEAColValue(col);
        }
      });

      const resolvedTimeLT =
        row.timeLT ?? getRawNMEARowTimeLT(flatRow) ?? undefined;

      if (row.dateTime || row.dateTimeLT || row.timeLT || resolvedTimeLT) {
        Object.assign(flatRow, {
          _ROW_DATE_TIME: row.dateTime ?? flatRow._ROW_DATE_TIME ?? "",
          _ROW_DATE_TIME_UTC: row.dateTimeUTC ?? "",
          _ROW_DATE_TIME_LT: row.dateTimeLT ?? "",
          _ROW_TIME_UTC: row.timeUTC ?? "",
          _ROW_TIME_LT: resolvedTimeLT ?? "",
        });
      }

      return [flatRow];
    }),
  );
};

/** Merges API rows with flattened saved NMEA data; API rows win on duplicate NMEAId. */
export const mergeNMEADataSources = (
  apiRows: Array<Record<string, any>>,
  savedNMEAData: INMEASection[] | undefined,
): Array<Record<string, any>> => {
  const savedFlat = flattenSavedNMEADataToRawRows(savedNMEAData);
  const seen = new Set<string>();
  const merged: Array<Record<string, any>> = [];

  apiRows.forEach(row => {
    const id = deriveNMEAId(row);
    seen.add(id);
    merged.push({ ...row, NMEAId: id });
  });

  savedFlat.forEach(row => {
    const id = deriveNMEAId(row);
    if (!seen.has(id)) {
      seen.add(id);
      merged.push({ ...row, NMEAId: id });
    }
  });

  return merged;
};

export const isNMEADataRowConsumedInSections = (
  dataRow: Record<string, any>,
  sections: ISection[],
): boolean => {
  const nmeaId = deriveNMEAId(dataRow);
  return sections.some(section =>
    section.rows.some(row =>
      getAllRowCols(row).some(col => col.NMEAId === nmeaId),
    ),
  );
};

/** True when a NMEA reading is already applied to a logbook row (by id or time). */
export const isNMEADataRowConsumedByTime = (
  dataRow: Record<string, any>,
  sections: ISection[],
  sectionIndex: number,
): boolean => {
  if (isNMEADataRowConsumedInSections(dataRow, sections)) return true;

  const dataTime = getRawNMEARowTimeLT(dataRow);
  if (!dataTime) return false;

  const section = sections[sectionIndex];
  if (!section) return false;

  return section.rows.some(row => {
    if (!getAllRowCols(row).some(col => col.NMEAId)) return false;

    const rowTime = getLogbookRowTimeLT(row);
    return rowTime ? nmeaTimesMatch(dataTime, rowTime) : false;
  });
};

export const getSectionScopedNMEAPool = (
  sectionIndex: number,
  nmeaData: Array<Record<string, any>>,
): Array<Record<string, any>> =>
  nmeaData.filter(row => {
    const savedSection = row._originalSectionIndex;
    return savedSection === undefined || savedSection === sectionIndex;
  });

export const isNMEARowMatchedInSection = (
  nmeaRow: INMEARow,
  section: ISection,
): boolean => {
  if (!section?.rows) return false;

  return section.rows.some(logbookRow => {
    const logbookCols = getAllRowCols(logbookRow);
    const hasNMEAId = logbookCols.some(col => col.NMEAId);
    if (!hasNMEAId) return false;

    if (logbookCols.some(col => col.NMEAId === nmeaRow.NMEAId)) {
      return true;
    }

    const timeCol = logbookCols.find(
      col => col.NMEA === "_ROW_DATE_TIME" || col.NMEA === "ROW_TIME",
    );
    const time =
      typeof timeCol?.value === "object" && timeCol?.value?.time
        ? timeCol.value.time
        : String(timeCol?.value ?? "");

    return nmeaRow.timeLT ? time === nmeaRow.timeLT : false;
  });
};

interface ISyncNMEADataAfterAmendParams {
  existingNMEAData: INMEASection[] | undefined;
  candidates: IAmendCandidateInput[];
  sections: ISection[];
}

export const syncNMEADataAfterAmend = ({
  existingNMEAData,
  candidates,
  sections,
}: ISyncNMEADataAfterAmendParams): INMEASection[] => {
  const sectionMap = new Map<number, INMEASection>(
    (existingNMEAData ?? []).map(section => [
      section.originalSectionIndex,
      structuredClone(section),
    ]),
  );

  candidates.forEach(candidate => {
    const section = sections[candidate.sectionIndex];
    if (!section) return;

    const nmeaDataRow = {
      ...candidate.nmeaDataRow,
      NMEAId: deriveNMEAId(candidate.nmeaDataRow),
    };

    const built = getOnlyNMEARows([section], [nmeaDataRow], 0, undefined);
    const incomingRow = built[0]?.rows[0];
    if (!incomingRow) return;

    incomingRow.selected = true;

    const existing = sectionMap.get(candidate.sectionIndex);
    if (existing) {
      sectionMap.set(candidate.sectionIndex, {
        ...existing,
        rows: mergeNMEARowsByTime(existing.rows, [incomingRow]),
      });
    } else {
      sectionMap.set(candidate.sectionIndex, {
        originalSectionIndex: candidate.sectionIndex,
        rows: [incomingRow],
      });
    }
  });

  return Array.from(sectionMap.values());
};

const getNMEAKey = (typedCol: ICol, dataRow: Record<string, any>): string => {
  const isRowTimeNMEA =
    typedCol.NMEA === "_ROW_DATE_TIME" || typedCol.NMEA === "ROW_TIME";

  if (isRowTimeNMEA && dataRow._ROW_DATE_TIME_LT) {
    return "_ROW_DATE_TIME_LT";
  }

  return typedCol.NMEA ?? "";
};

const getNMEARowTimeKey = (row?: INMEARow | null): string => {
  if (!row) return "";

  return (
    row.dateTimeLT ??
    row.timeLT ??
    row.dateTimeUTC ??
    row.timeUTC ??
    row.dateTime ??
    ""
  );
};

const findNMEARowIndexByTime = (
  rows: INMEARow[],
  incomingRow: INMEARow,
): number => {
  const incomingTimeKey = getNMEARowTimeKey(incomingRow);
  if (!incomingTimeKey) return -1;

  return rows.findIndex(row => {
    const existingTimeKey = getNMEARowTimeKey(row);
    if (existingTimeKey && existingTimeKey === incomingTimeKey) return true;
    return nmeaTimesMatch(existingTimeKey, incomingTimeKey);
  });
};

const mergeNMEARowsByTime = (
  existingRows: INMEARow[],
  incomingRows: INMEARow[],
): INMEARow[] => {
  const mergedRows = [...existingRows];

  incomingRows.forEach(incomingRow => {
    const incomingTimeKey = getNMEARowTimeKey(incomingRow);
    if (!incomingTimeKey) return;

    const matchingRowIndex = findNMEARowIndexByTime(mergedRows, incomingRow);

    const normalizedIncomingRow = {
      ...incomingRow,
      selected: true,
    };

    if (matchingRowIndex !== -1) {
      mergedRows[matchingRowIndex] = {
        ...mergedRows[matchingRowIndex],
        ...normalizedIncomingRow,
      };
      return;
    }

    mergedRows.push(normalizedIncomingRow);
  });

  return sortNMEARowsByTime(mergedRows);
};

/**
 * Filters and retrieves only NMEA filled rows from the provided sections.
 *
 * @param sections - The sections to filter rows from.
 * @param newData - The new data to match against the NMEA rows.
 * @param sectionIndex - The index of the section to process.
 * @param triggeredBy - The trigger type ('add', 'refresh', or undefined for new entry).
 * @returns An array of filtered NMEA sections.
 */
export const getOnlyNMEARows = (
  sections: ISection[],
  newData: Array<Record<string, any>>,
  sectionIndex: number | undefined,
  triggeredBy?: string,
): INMEASection[] => {
  const filteredSections: INMEASection[] = [];
  sections.forEach((section, currentSectionIndex) => {
    // For add mode, only process the specific section where add was clicked
    if (
      triggeredBy === "add" &&
      sectionIndex !== undefined &&
      currentSectionIndex !== sectionIndex
    ) {
      return;
    }

    const newRows: INMEARow[] = [];

    newData.forEach((dataRow, rowIdx) => {
      // For add mode, show all rows but filter out existing ones
      if (triggeredBy === "add" && sectionIndex === currentSectionIndex) {
        const derivedId = deriveNMEAId(dataRow);

        // Check if this row already exists in the section by time or NMEA ID
        const rowAlreadyExists = section.rows.some(existingRow => {
          const existingRowCols = getAllRowCols(existingRow);

          const hasNMEAId = existingRowCols.some((col: any) => col.NMEAId);
          if (!hasNMEAId) return false;

          if (existingRowCols.some((col: any) => col.NMEAId === derivedId)) {
            return true;
          }

          // Check by time
          const timeCol = existingRowCols.find(
            (col: any) =>
              col.NMEA === "_ROW_DATE_TIME" || col.NMEA === "ROW_TIME",
          );
          if (timeCol?.value) {
            const timeVal = timeCol.value;
            const existingTimeLT =
              typeof timeVal === "object" && timeVal.time
                ? timeVal.time
                : String(timeVal);

            // Compare against incoming LT time (rows are stored in LT)
            const incomingTimeLT = dataRow._ROW_TIME_LT || "";

            if (existingTimeLT === incomingTimeLT) {
              return true;
            }
          }

          return false;
        });

        if (rowAlreadyExists) return;
      } else if (
        (!section.dynamicRows || sectionIndex !== undefined) &&
        rowIdx > 0
      ) {
        return;
      }

      const nmeaId = deriveNMEAId(dataRow);
      const resolvedTimeLT = getRawNMEARowTimeLT(dataRow);
      const enrichedDataRow = resolvedTimeLT
        ? {
            ...dataRow,
            _ROW_TIME_LT: dataRow._ROW_TIME_LT || resolvedTimeLT,
            NMEAId: dataRow.NMEAId ?? dataRow._NMEAId ?? nmeaId,
          }
        : dataRow;
      const dateTimeObj = {
        dateTime: enrichedDataRow._ROW_DATE_TIME || "",
        dateTimeLT: enrichedDataRow._ROW_DATE_TIME_LT || "",
        dateTimeUTC: enrichedDataRow._ROW_DATE_TIME_UTC || "",
        timeLT:
          enrichedDataRow._ROW_TIME_LT?.indexOf(":") !== -1
            ? enrichedDataRow._ROW_TIME_LT
            : enrichedDataRow._ROW_DATE_TIME_LT?.split(" ")?.[3] || "",
        timeUTC:
          enrichedDataRow._ROW_TIME_UTC?.indexOf(":") !== -1
            ? enrichedDataRow._ROW_TIME_UTC
            : enrichedDataRow._ROW_DATE_TIME_UTC?.split(" ")?.[3] || "",
      };
      let newRow: INMEARow = {
        cols: {},
        NMEAId: nmeaId,
      };
      if (!section.rows[0]) return;
      let hasTimeCol = false;
      getAllRowCols(section.rows[0]).forEach(col => {
        const NMEAKey = getNMEAKey(col, enrichedDataRow);
        if (col.NMEA && enrichedDataRow[NMEAKey]) {
          const isTimeCol =
            col.NMEA === "_ROW_DATE_TIME" || col.NMEA === "ROW_TIME";
          const value = isTimeCol
            ? parseNMEAValue(col, enrichedDataRow).value
            : enrichedDataRow[NMEAKey];

          newRow.cols[col.NMEA] = {
            ...col,
            value,
          };

          if (isTimeCol) {
            hasTimeCol = true;
          }
        }
      });
      newRow = { ...newRow, ...(hasTimeCol ? dateTimeObj : {}) };

      if (Object.values(newRow.cols).length > 0) newRows.push(newRow);
    });
    if (
      (sectionIndex !== undefined && currentSectionIndex === sectionIndex) ||
      sectionIndex === undefined
    ) {
      filteredSections.push({
        rows: newRows,
        originalSectionIndex: currentSectionIndex,
      });
    }
  });
  return filteredSections;
};
interface IParseNMEADataForSaving {
  sectionsWithSelectedProp: INMEASection[];
  sections: ISection[];
  NMEAData: INMEASection[] | undefined;
  NMEAModalData: INMEAAutomationModalData | null;
}
export const parseNMEADataForSaving = ({
  sectionsWithSelectedProp,
  sections,
  NMEAModalData,
  NMEAData,
}: IParseNMEADataForSaving) => {
  let newNMEAData: INMEASection[] = sectionsWithSelectedProp;
  if (NMEAModalData?.triggeredBy && NMEAData?.length) {
    newNMEAData = NMEAData.map((s, i) => {
      const parsedSection: INMEASection = structuredClone(s);
      const matchedNewSection = sectionsWithSelectedProp.find(
        el => el.originalSectionIndex === s.originalSectionIndex,
      );
      if (!matchedNewSection) return parsedSection;
      if (NMEAModalData.triggeredBy === "add") {
        return {
          ...parsedSection,
          rows: mergeNMEARowsByTime(parsedSection.rows, matchedNewSection.rows),
        };
      }
      // parsing for refresh trigger
      if (
        NMEAModalData?.sectionIndex === undefined ||
        NMEAModalData?.rowIndex === undefined ||
        NMEAModalData?.sectionIndex !== s.originalSectionIndex
      )
        return parsedSection;

      const logbookRow =
        sections[NMEAModalData.sectionIndex].rows[NMEAModalData.rowIndex];

      if (!logbookRow) return parsedSection;

      const NMEAId = getAllRowCols(logbookRow).find(
        (col: any) => col?.NMEAId,
      )?.NMEAId;

      if (NMEAId) {
        const matchedNMEARowToUnselect = parsedSection.rows.find(
          r => r && r.NMEAId === NMEAId,
        );
        if (matchedNMEARowToUnselect) {
          matchedNMEARowToUnselect.selected = false;
        }
      }
      return {
        ...parsedSection,
        rows: mergeNMEARowsByTime(parsedSection.rows, matchedNewSection.rows),
      };
    });
  }
  return newNMEAData;
};

/**
 * Cleans up NMEA data by removing selections for rows that no longer exist in the logbook
 * @param nmeaData - Current NMEA data
 * @param sections - Current logbook sections
 * @returns Cleaned NMEA data with invalid selections removed
 */
export const cleanupInvalidNMEASelections = (
  nmeaData: INMEASection[] | undefined,
  sections: ISection[],
): INMEASection[] | undefined => {
  if (!nmeaData) return nmeaData;

  return nmeaData.map(nmeaSection => {
    const section = sections[nmeaSection.originalSectionIndex];
    if (!section?.rows) {
      // If section doesn't exist, unselect all rows
      return {
        ...nmeaSection,
        rows: nmeaSection.rows.map(row => ({
          ...row,
          selected: false,
        })),
      };
    }

    // Check each NMEA row to see if it still corresponds to an existing logbook row
    const cleanedRows = nmeaSection.rows.map(nmeaRow => {
      if (!nmeaRow) return nmeaRow;

      const stillExists = section.rows.some(logbookRow => {
        const logbookCols = getAllRowCols(logbookRow);
        const hasNMEAId = logbookCols.some(col => col.NMEAId);
        if (!hasNMEAId) return false;

        const timeCol = logbookCols.find(
          col => col.NMEA === "_ROW_DATE_TIME" || col.NMEA === "ROW_TIME",
        );
        const time =
          typeof timeCol?.value === "object" && timeCol?.value?.time
            ? timeCol.value.time
            : String(timeCol?.value ?? "");

        return nmeaRow.timeLT ? time === nmeaRow.timeLT : true;
      });
      return {
        ...nmeaRow,
        selected: stillExists,
      };
    });

    return {
      ...nmeaSection,
      rows: cleanedRows,
    };
  });
};

export const formatDateTimeUTC = (dateTime: any): any => {
  const date = new Date(dateTime);
  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  if (date.toString() !== "Invalid Date") {
    return formatter.format(date).replace(",", "");
  } else if (typeof dateTime === "string") {
    return dateTime;
  } else {
    return null;
  }
};
